import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"

type RecordingRef = { id: number; title: string; slug: string }
type ChartListingFormProps = { id: number | null; recording_id: number; chart_date: string | null; position: number | null }
type PageProps = { chart_listing: ChartListingFormProps; recording: RecordingRef; errors?: Record<string, string> }

const ic = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-primary"

export default function ChartListingForm({ chart_listing, recording }: { chart_listing: ChartListingFormProps; recording: RecordingRef }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = chart_listing.id === null
  const [form, setForm] = useState({
    chart_date: chart_listing.chart_date ?? "",
    position:   chart_listing.position !== null ? String(chart_listing.position) : "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isNew) router.post(`/recordings/${recording.slug}/chart_listings`, { chart_listing: form })
    else       router.patch(`/chart_listings/${chart_listing.id}`, { chart_listing: form })
  }

  const title = isNew ? "New chart listing" : "Edit chart listing"

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`${title} for ${recording.title}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`${title} for ${recording.title}`} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
          <p className="mt-1">For <Link href={`/recordings/${recording.slug}`}>{recording.title}</Link></p>
        </div>
        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
          <div>
            <label className="block text-sm text-ink-muted mb-1">Position</label>
            <input name="position" type="number" value={form.position} onChange={handleChange} className={ic} />
            {errors.position && <p className="mt-1 text-xs text-red-600">{errors.position}</p>}
          </div>
          <div>
            <label className="block text-sm text-ink-muted mb-1">Chart date</label>
            <input name="chart_date" type="date" value={form.chart_date ?? ""} onChange={handleChange} className={ic} />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create chart listing" : "Update chart listing"}</Button>
            <Link href={`/recordings/${recording.slug}`} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
